import React from 'react';
import * as ReactDOM from 'react-dom';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Icons, Row } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';
import { getResponseError, setFormError } from '../../../utils/utils';
import { FORM_VALIDATION_MESSAGE } from '../../../utils/apiError';
import { createPolicies, editPoliciesById } from '../actions';
import Loader from '../../../components/base/Loader/Loader';
import { getPoliciesData } from '../reducer';
import PoliciesDTO, {
  POLICY_CURRENCY_LIST,
  normalizePolicyArray,
  normalizePolicyPayload,
} from '../../../dto/PoliciesDTO';
import FieldDTOWithCol from '../../../components/fields/Abstract/FieldDTOWithCol';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import { loadRulesList } from '../../Rules/actions';
import SelectGeographies from '../../Rules/components/SelectGeographies';
import { loadProviderList } from '../../Provider/actions';
import DatePicker from '../../../components/fields/DatePicker/DatePicker';
import { getRulesData } from '../../Rules/reducer';
import CustomSelectInput from '../../../components/fields/SelectInput/CustomSelectInput';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const PROVIDER_NOT_FOUND_TEXT = 'Провайдер не найден';

const getRuleId = rule => (rule && typeof rule === 'object' ? rule.rule_id || rule.id : rule);
const getProviderFallbackOptions = policy => {
  const providerNamesById = new Map((policy?.providers || []).map(provider => [String(provider.id), provider.name]));
  return (policy?.provider_ids || []).map(providerId => ({
    value: providerId,
    name: providerNamesById.get(String(providerId)) || PROVIDER_NOT_FOUND_TEXT,
  }));
};

const getPolicySaveError = response => getResponseError(response);

export default function PoliciesForm(props) {
  const {
    onClose = () => {},
    loadData = () => {},
    currentPolicies = null,
  } = props;
  const { loadingCreatePolicies, loadingEditPoliciesById } = useSelector(getPoliciesData);
  const { rulesList } = useSelector(getRulesData);
  const dispatch = useDispatch();
  const defaultValues = { ...currentPolicies } || {};
  if (defaultValues?.rules?.length) {
    defaultValues.rules = defaultValues?.rules.map(getRuleId).filter(Boolean);
  }
  const {
    control, getValues, setError, clearErrors,
  } = useForm({
    mode: 'onChange',
    defaultValues,
  });

  const setPolicyError = (name, message, shouldFocus = false) => {
    setError(name, { type: 'manual', message }, { shouldFocus });
  };

  const getResolvedRules = value => {
    const selectedRules = normalizePolicyArray(value);
    const sourceRules = [
      ...normalizePolicyArray(rulesList),
      ...normalizePolicyArray(currentPolicies?.rules),
    ];
    return selectedRules.map(rule => {
      if (rule && typeof rule === 'object') {
        return rule;
      }
      return sourceRules.find(sourceRule => String(getRuleId(sourceRule)) === String(rule));
    }).filter(Boolean);
  };

  const getValidatedPayload = () => {
    clearErrors();
    const values = { ...getValues() };
    const resolvedRules = getResolvedRules(values.rules);
    const selectedRules = normalizePolicyArray(values.rules);
    values.rules = resolvedRules;

    let payload;
    try {
      payload = normalizePolicyPayload(values);
    } catch (e) {
      setPolicyError(e.fieldName || 'start_date', e.message || 'Выберите корректную дату', true);
      errorNotification('Сохранение формы', FORM_VALIDATION_MESSAGE);
      return null;
    }

    const errors = [];
    if (!payload.name) {
      errors.push(['name', 'Введите название политики']);
    }
    if (!payload.description) {
      errors.push(['description', 'Введите описание политики']);
    }
    if (payload.start_date === null) {
      errors.push(['start_date', 'Выберите дату начала']);
    }
    if (payload.published_date === null) {
      errors.push(['published_date', 'Выберите дату публикации']);
    }
    if (!selectedRules.length || !payload.rules.length || payload.rules.length !== selectedRules.length) {
      errors.push(['rules', 'Выберите правила']);
    }
    if (payload.provider_ids?.some(providerId => !UUID_RE.test(String(providerId)))) {
      errors.push(['provider_ids', 'Выберите корректных провайдеров']);
    }
    if (payload.rules?.some(rule => !rule || typeof rule !== 'object' || !UUID_RE.test(String(getRuleId(rule))))) {
      errors.push(['rules', 'Выберите корректные правила']);
    }

    if (errors.length) {
      errors.forEach(([name, message], index) => setPolicyError(name, message, index === 0));
      errorNotification('Сохранение формы', FORM_VALIDATION_MESSAGE);
      return null;
    }
    return payload;
  };

  const handleSave = () => {
    const payload = getValidatedPayload();
    if (!payload) {
      return;
    }
    const action = currentPolicies?.policy_id ? editPoliciesById({ ...payload, id: currentPolicies.policy_id }) : createPolicies(payload);
    dispatch(action)
      .then(() => {
        successNotification('Сохранение формы', 'Форма сохранена');
        loadData();
        onClose();
      })
      .catch(response => {
        setFormError(response, setError);
        errorNotification('Сохранение формы', getPolicySaveError(response));
      });
  };

  const dto = new PoliciesDTO(null, false, { withRequired: true });
  const attributes = dto.getAttributesByName();
  const currentCurrency = defaultValues?.currency ? String(defaultValues.currency) : '';
  const currencyOptions = currentCurrency && !POLICY_CURRENCY_LIST.some(item => String(item.value) === currentCurrency)
    ? [{ value: currentCurrency, name: currentCurrency }, ...POLICY_CURRENCY_LIST]
    : POLICY_CURRENCY_LIST;
  const providerFallbackOptions = getProviderFallbackOptions(currentPolicies);

  return ReactDOM.createPortal(
    <div className="form-dialog policies-form">
      <Loader loading={loadingCreatePolicies || loadingEditPoliciesById} />
      <div className="form-header">
        <div className="breadcrumbs">
          <span className="breadcrumbs-prev">Политики</span>
          <Icons width={22} height={22} customIcon icon="arrow-right" className="breadcrumbs-arrow" />
          <span className="breadcrumbs-current">Политика № {currentPolicies?.policy_id || 'Без номера'}</span>
          <span className="breadcrumbs-current"> ({currentPolicies?.policy_id ? 'редактирование' : 'создание'}) </span>

        </div>
        <div className="close-button" onClick={onClose}>
          <Icons width={24} height={24} customIcon icon="close" />
        </div>
      </div>
      <div className="form-body">
        <form className="form-content">
          <div className="form-fields">
            <div className="form-fields-wrapper custom-scroll-theme">
              <div className="afc-body">
                <div className="afc-main policies-main-info">
                  <Row className="row-cols-1">
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={TextInputWithLabel}
                      dtoObject={attributes.name}
                      variant="material"
                      withLabel
                      className="mb-3"
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={TextInputWithLabel}
                      dtoObject={attributes.description}
                      variant="material"
                      withLabel
                      className="mb-3"
                      textarea
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={CustomSelectInput}
                      dtoObject={attributes.currency}
                      variant="material"
                      withLabel
                      options={currencyOptions}
                      addPlaceholder
                      useDefaultNull
                      className="mb-3"
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={DatePicker}
                      hideTime
                      dtoObject={attributes.start_date}
                      variant="material"
                      withLabel
                      className="mb-3"
                      changeFormat="x"
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={DatePicker}
                      hideTime
                      dtoObject={attributes.end_date}
                      variant="material"
                      withLabel
                      className="mb-3"
                      changeFormat="x"
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={DatePicker}
                      hideTime
                      dtoObject={attributes.published_date}
                      variant="material"
                      withLabel
                      className="mb-3"
                      changeFormat="x"
                    />
                    {/* <FieldDTOWithCol */}
                    {/*  control={control} */}
                    {/*  type="text" */}
                    {/*  component={CustomSelectInput} */}
                    {/*  dtoObject={attributes.provider_ids} */}
                    {/*  variant="material" */}
                    {/*  withLabel */}
                    {/*  options={PROVIDER_LIST} */}
                    {/*  className="mb-3" */}
                    {/* /> */}
                    <FieldDTOWithCol
                      control={control}
                      dtoObject={attributes.provider_ids}
                      usePortal
                      component={SelectGeographies}
                      portalClassName="executor-selector"
                      loadAction={loadProviderList}
                      optionFormatter={item => ({ value: item.id, name: item.name })}
                      fallbackOptions={providerFallbackOptions}
                      filterName="full_name"
                      variant="material"
                      withLabel
                      className="mb-3"
                      multiple
                    />
                    <FieldDTOWithCol
                      control={control}
                      dtoObject={attributes.rules}
                      usePortal
                      addPlaceholder
                      // component={CustomSelectInputWithHelper}
                      component={SelectGeographies}
                      portalClassName="executor-selector"
                      loadAction={loadRulesList}
                      optionFormatter={item => ({ value: item.rule_id, name: item.name })}
                      filterName="full_name"
                      variant="material"
                      withLabel
                      className="mb-3"
                      multiple
                    />
                  </Row>
                </div>
              </div>
            </div>
          </div>
          <div className="form-footer">
            <Button onClick={() => handleSave()}>{currentPolicies?.policy_id ? 'Сохранить' : 'Создать'}</Button>
            <Button variant="secondary" onClick={onClose}>Отменить</Button>
          </div>
        </form>
      </div>
    </div>,
    document.getElementById('app-content'),
  );
}
